// Tab "Công nợ của tôi": vì sao nợ (từng khoản theo đơn), khoản sắp phát sinh, tiền đã trả và
// hướng dẫn chuyển khoản trả nợ. Không có hạn mức, không chặn đơn (D17) — khoản âm được tự trừ
// vào tiền các đơn sau; chuyển khoản đúng nội dung để ShopTrack ghi nhận và đóng công nợ.

import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2 } from 'lucide-react'
import { get } from '@/lib/api'
import { formatDate, formatDateTime, money } from '@/lib/format'
import { cn } from '@/lib/utils'
import { LoadError } from '@/components/ui/data-notice'
import { Section } from '@/components/ui/section'
import { Skeleton } from '@/components/ui/skeleton'
import DataTable, { type DataTableColumn } from '@/components/ui/data-table'
import { useNppName } from '@/components/layout/shell-model'
import { CopyButton } from './parts'
import { debtReason, outcomeLabel, type DebtLine, type DebtPayment, type MyDebt } from './types'

export function ReconcileDebt() {
  const q = useQuery({ queryKey: ['reconcile', 'debt'], queryFn: () => get<MyDebt>('/reconcile/debt') })
  const nppName = useNppName()

  if (q.isError) {
    return (
      <Section>
        <LoadError onRetry={() => q.refetch()}>Không tải được công nợ.</LoadError>
      </Section>
    )
  }
  const d = q.data
  if (q.isLoading || !d) {
    return (
      <section className="space-y-3 rounded-2xl bg-band p-5 sm:p-6">
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </section>
    )
  }

  const owing = d.lines.filter((l) => l.status === 'payable')
  const upcoming = d.lines.filter((l) => l.status === 'estimated')
  const upcomingTotal = -upcoming.reduce((s, l) => s + l.amount, 0)
  const hasDebt = d.debt > 0

  return (
    <div className="space-y-5">
      <section className="space-y-4 rounded-2xl bg-band p-5 sm:p-6">
        {hasDebt ? (
          <>
            <h2 className="!text-[1.35rem] leading-snug text-ink mobile:!text-[1.15rem]">
              Bạn đang nợ {nppName} <span className="tabular-nums text-danger">{money(d.debt)}</span>
            </h2>
          </>
        ) : (
          <h2 className="flex items-center gap-2 !text-[1.35rem] leading-snug text-ink mobile:!text-[1.15rem]">
            <CheckCircle2 className="size-6 shrink-0 text-ok" aria-hidden /> Bạn không có công nợ
          </h2>
        )}

        {hasDebt && <TransferBox amount={d.debt} transfer={d.transfer} />}
      </section>

      {owing.length > 0 && (
        <Section title={hasDebt ? `Khoản đang nợ (${owing.length})` : `Khoản âm trừ vào lần chi tới (${owing.length})`}>
          <DebtLinesTable lines={owing} />
        </Section>
      )}

      {upcoming.length > 0 && (
        <Section title={`Sắp phát sinh · ${money(upcomingTotal)}`}>
          <DebtLinesTable lines={upcoming} />
        </Section>
      )}

      {d.payments.length > 0 && (
        <Section title="Bạn đã trả nợ">
          <PaymentsTable payments={d.payments} />
        </Section>
      )}
    </div>
  )
}

function TransferBox({ amount, transfer }: { amount: number; transfer: MyDebt['transfer'] }) {
  const nppName = useNppName()
  return (
    <div className="space-y-3 rounded-xl bg-raise p-4 shadow-card">
      <p className="text-sm font-medium text-ink">Chuyển khoản trả nợ</p>
      {transfer.accountNumber ? (
        <dl className="space-y-2.5 text-sm">
          <TransferRow label="Ngân hàng">{transfer.bank ?? '—'}</TransferRow>
          <TransferRow label="Số tài khoản" copy={{ value: transfer.accountNumber, label: 'số tài khoản' }}>
            <span className="font-mono text-base font-semibold tracking-wide">{transfer.accountNumber}</span>
          </TransferRow>
          <TransferRow label="Chủ tài khoản">{transfer.holder ?? '—'}</TransferRow>
          <TransferRow label="Số tiền" copy={{ value: String(amount), label: 'số tiền' }}>
            <span className="font-mono font-semibold tabular-nums">{money(amount)}</span>
          </TransferRow>
          <TransferRow label="Nội dung" copy={{ value: transfer.content, label: 'nội dung chuyển khoản' }}>
            <span className="font-mono text-base font-semibold tracking-wide text-accent">{transfer.content}</span>
          </TransferRow>
        </dl>
      ) : (
        <p className="rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">
          {nppName} chưa cấu hình tài khoản nhận trả nợ.
        </p>
      )}
      <p className="text-xs text-inkSoft">
        Ghi <strong>đúng nội dung</strong> như trên.
      </p>
    </div>
  )
}

function TransferRow({ label, copy, children }: { label: string; copy?: { value: string; label: string }; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <dt className="w-28 shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 flex-1 break-all text-ink">{children}</dd>
      {copy && <CopyButton value={copy.value} label={copy.label} />}
    </div>
  )
}

function DebtLinesTable({ lines }: { lines: DebtLine[] }) {
  const nppName = useNppName()
  const columns: DataTableColumn<DebtLine>[] = [
    {
      key: 'reason',
      header: 'Vì sao',
      priority: 'primary',
      cell: (l) => (
        <span className="block min-w-0">
          <span className="block text-ink">{debtReason(l, nppName)}</span>
          {l.order && (
            <span className="block text-xs text-muted">
              <span className="font-mono">{l.order.waybillCode ?? l.order.orderId ?? '—'}</span> · {outcomeLabel(l.order.status, l.order.returnStage)}
              {l.order.codExpected > 0 ? ` · COD ${money(l.order.codExpected)}` : ' · không thu COD'}
            </span>
          )}
        </span>
      ),
    },
    { key: 'date', header: 'Ngày', cell: (l) => <span className="whitespace-nowrap tabular-nums text-inkSoft">{formatDate(l.order?.deliveredAt ?? l.order?.returnCompletedAt ?? l.createdAt)}</span> },
    {
      key: 'amount',
      header: 'Số tiền',
      align: 'right',
      cell: (l) => <span className={cn('whitespace-nowrap font-mono font-semibold tabular-nums', l.amount < 0 ? 'text-danger' : 'text-ink')}>{money(l.amount)}</span>,
    },
  ]
  return <DataTable columns={columns} data={lines} rowKey={(l) => l.id} />
}

/** Cách trả nợ — "ghi nhận tay" là kế toán của NPP (Sunrise: "ShopTrack"). */
const methodLabel = (method: string, nppName: string): string =>
  method === 'sepay' ? 'Chuyển khoản — tự khớp' : method === 'manual' ? `Chuyển khoản — ${nppName} ghi nhận` : method

function PaymentsTable({ payments }: { payments: DebtPayment[] }) {
  const nppName = useNppName()
  const columns: DataTableColumn<DebtPayment>[] = [
    { key: 'date', header: 'Ngày', priority: 'primary', cell: (p) => <span className="whitespace-nowrap tabular-nums text-ink">{formatDateTime(p.createdAt)}</span> },
    { key: 'method', header: 'Cách trả', cell: (p) => <span className="text-inkSoft">{methodLabel(p.method, nppName)}</span> },
    { key: 'ref', header: 'Mã giao dịch', cell: (p) => <span className="font-mono text-xs text-inkSoft">{p.bankRef ?? '—'}</span> },
    { key: 'amount', header: 'Số tiền', align: 'right', cell: (p) => <span className="whitespace-nowrap font-mono font-semibold tabular-nums text-ok">+{money(p.amount)}</span> },
  ]
  return <DataTable columns={columns} data={payments} rowKey={(p) => p.id} />
}
